import {
  createRootRoute,
  createRoute,
  createRouter,
  RouterProvider,
} from '@tanstack/react-router';
import { Layout, PageLayout } from '@city-sage/aurum-avis-labs-components';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { RepositoryProvider } from '@city-sage/aurum-avis-labs-components';
import { lazy, Suspense } from 'react';
import { Skeleton } from '@city-sage/aurum-avis-labs-components';
import {
  AuthGuard,
  LoginProvider,
} from '@city-sage/aurum-avis-labs-components';
import { Sonner, TooltipProvider } from '@city-sage/aurum-avis-labs-components';
import { CookieBanner } from '@city-sage/aurum-avis-labs-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@city-sage/aurum-avis-labs-components';
import LogRocket from 'logrocket';
import { AccountNameGuard } from './components/AccountNameGuard';
import { z } from 'zod';
import { ChatProvider } from './components/chat/ChatProvider';
import { useBasicLogRocketIdentification } from './hooks/useLogRocketIdentification';
import { LogRocketAuthIdentifier } from './components/LogRocketAuthIdentifier';

// ===== PUBLIC ROUTES (Preloaded for optimal UX) =====
import LandingPage from './components/pages/LandingPage';
import PublicProfilePage from './components/pages/PublicProfilePage';
import HelpPage from './components/pages/HelpPage';
import TermsPage from './components/pages/TermsPage';
import PrivacyPage from './components/pages/PrivacyPage';
import ItineraryPage from './components/pages/ItineraryPage';
import { ItineraryReelContainer } from './components/public-itinerary/reel/ItineraryReelContainer';
import { SearchPage } from './components/pages/SearchPage';

// ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded) =====
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'));
const CreateItineraryPage = lazy(() => import('./components/pages/CreateItineraryPage').then(m => ({ default: m.CreateItineraryPage })));
const StopFormPage = lazy(() => import('./components/pages/StopFormPage').then(m => ({ default: m.StopFormPage })));
const ItineraryEditPage = lazy(() => import('./components/pages/ItineraryEditPage'));
const StopPage = lazy(() => import('./components/pages/StopPage'));
const MyItinerariesPage = lazy(() => import('./components/pages/MyItinerariesPage'));
const CreateManualItineraryPage = lazy(() => import('./components/pages/CreateManualItineraryPage').then(m => ({ default: m.CreateManualItineraryPage })));
const ItineraryDayEditPage = lazy(() => import('./components/pages/ItineraryDayEditPage'));

// Loading component for authenticated routes
const AuthRouteLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="space-y-4 w-full max-w-md">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  </div>
);

interface AppProps {
  msalInstance: PublicClientApplication;
}

// Create a wrapper component that includes ChatProvider and Layout
function AppLayout() {
  return (
    <TooltipProvider>
      <AuthProvider>
        <LogRocketAuthIdentifier />
        <LoginProvider>
          <AuthGuard>
            <QueryClientProvider client={queryClient}>
              <RepositoryProvider>
                <AccountNameGuard>
                  <ChatProvider>
                    <Layout />
                  </ChatProvider>
                </AccountNameGuard>
              </RepositoryProvider>
            </QueryClientProvider>
          </AuthGuard>
        </LoginProvider>
      </AuthProvider>
    </TooltipProvider>
  );
}

// Helper function to wrap components with PageLayout
function withPageLayout(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    return (
      <PageLayout>
        <Component {...props} />
      </PageLayout>
    );
  };
}

// Helper function to wrap authenticated components with PageLayout and Suspense
function withAuthPageLayout(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    return (
      <PageLayout>
        <Suspense fallback={<AuthRouteLoading />}>
          <Component {...props} />
        </Suspense>
      </PageLayout>
    );
  };
}

// Define the root route
const rootRoute = createRootRoute({
  component: AppLayout,
  validateSearch: z.object({
    previewItineraryId: z.string().uuid().optional(),
  }),
});

// Landing page - direct child of root (full width)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
  validateSearch: z.object({
    bounds: z.string().optional(), // JSON stringified bounds for filtering itineraries
  }),
});

// ===== PUBLIC ROUTES (Preloaded, standard PageLayout) =====
const publicProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  component: withPageLayout(PublicProfilePage),
});

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: withPageLayout(HelpPage),
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: withPageLayout(SearchPage),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: withPageLayout(TermsPage),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: withPageLayout(PrivacyPage),
});

const itineraryViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$id',
  component: ItineraryPage,
});

// ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded with Suspense) =====
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: withAuthPageLayout(ProfilePage),
});

const createItineraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/create',
  component: withAuthPageLayout(CreateItineraryPage),
});

const createManualItineraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/create/manual',
  component: withAuthPageLayout(CreateManualItineraryPage),
});

const itineraryEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$id/edit',
  component: withAuthPageLayout(ItineraryEditPage),
});

const itineraryDayEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$id/days/$dayId/edit',
  component: withAuthPageLayout(ItineraryDayEditPage),
});

const stopFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/days/$dayId/stops/$stopId/form',
  component: withAuthPageLayout(() => {
    const { itineraryId, dayId, stopId } = stopFormRoute.useParams();
    return (
      <StopFormPage itineraryId={itineraryId} dayId={dayId} stopId={stopId} />
    );
  }),
});

const stopCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/days/$dayId/stops/form',
  component: withAuthPageLayout(() => {
    const { itineraryId, dayId } = stopCreateRoute.useParams();
    return <StopFormPage itineraryId={itineraryId} dayId={dayId} />;
  }),
});

const stopViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/stops/$stopId',
  component: withAuthPageLayout(StopPage),
});

const myItinerariesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-itineraries',
  component: withAuthPageLayout(MyItinerariesPage),
});

// Reel route - full screen like landing page (no PageLayout)
const reelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/reels/$startId',
  component: () => {
    const { startId } = reelRoute.useParams();
    const search = reelRoute.useSearch();

    // Parse bounds from search params
    const bounds = search.bounds ? JSON.parse(search.bounds) : undefined;
    const initialActiveIndex = search.activeIndex;

    return (
      <ItineraryReelContainer
        initialItineraryId={startId}
        bounds={bounds}
        initialActiveIndex={initialActiveIndex}
      />
    );
  },
  validateSearch: z.object({
    bounds: z.string().optional(), // JSON stringified bounds
    activeIndex: z.coerce.number().optional(), // Current active story index
  }),
});

// Create the router instance
const router = createRouter({
  routeTree: rootRoute.addChildren([
    // ===== FULL-WIDTH ROUTES (No PageLayout wrapper) =====
    indexRoute, // Landing page - direct child (full width)
    reelRoute, // Reel page - direct child (full width)
    
    // ===== PUBLIC ROUTES (Preloaded with PageLayout) =====
    publicProfileRoute,
    helpRoute,
    searchRoute,
    termsRoute,
    privacyRoute,
    itineraryViewRoute,
    
    // ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded with PageLayout + Suspense) =====
    profileRoute,
    createItineraryRoute,
    createManualItineraryRoute,
    itineraryEditRoute,
    itineraryDayEditRoute,
    stopFormRoute,
    stopCreateRoute,
    stopViewRoute,
    myItinerariesRoute,
  ]),
  scrollRestoration: true,
  // For mobile apps, you might need to target specific scrollable containers
  scrollToTopSelectors: ['body'],
});

// Initialize LogRocket only in non-development environments
console.log('[LogRocket] Environment check:', {
  isDev: import.meta.env.DEV,
  mode: import.meta.env.MODE,
  shouldInit: !import.meta.env.DEV,
});

if (!import.meta.env.DEV) {
  console.log(
    '[LogRocket] Initializing LogRocket with app ID: lf70zj/citysage'
  );
  try {
    LogRocket.init('lf70zj/citysage');
    console.log('[LogRocket] Successfully initialized');
  } catch (error) {
    console.error('[LogRocket] Failed to initialize:', error);
  }
} else {
  console.log('[LogRocket] Skipping initialization in development mode');
}
const queryClient = new QueryClient();

// Component to handle LogRocket user identification
function LogRocketUserIdentifier() {
  useBasicLogRocketIdentification();
  return null;
}

function App({ msalInstance }: AppProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <LogRocketUserIdentifier />
      <Sonner />
      <RouterProvider router={router} />
      <CookieBanner />
    </MsalProvider>
  );
}

export default App;
